import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { PageShell } from "@/components/site/PageShell";
import { SeoHead } from "@/components/SeoHead";

const FALLBACK = {
    name: "G21 Projektentwicklung",
    phone: "+49 611 000000",
    email: "info@g21-wiesbaden.de",
    address: "Gaugasse 21, 65203 Wiesbaden",
};

export default function Datenschutz() {
    const [c, setC] = useState(FALLBACK);
    useEffect(() => {
        api.get("/company").then((r) => setC(r.data || FALLBACK)).catch(() => setC(FALLBACK));
    }, []);

    return (
        <PageShell testId="datenschutz-page">
            <SeoHead
                seo={{
                    title: "Datenschutzerklärung · G21 Neubau Wiesbaden-Biebrich",
                    description: "Datenschutzerklärung der G21 Projekt GmbH – Informationen zur Verarbeitung personenbezogener Daten beim Neubauprojekt Gaugasse 21, Wiesbaden.",
                    canonical: "https://neubau-kaufen-wiesbaden.de/datenschutz",
                    ogImage: "https://neubau-kaufen-wiesbaden.de/og-cover.jpg",
                    lang: "de",
                }}
            />
            <div className="py-16">
            <div className="container-page max-w-3xl">
                <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-ink mb-10">
                    <ArrowLeft size={16} /> Zurück
                </Link>

                <span className="overline">Rechtliches</span>
                <h1 className="font-heading text-4xl sm:text-5xl text-ink mt-4 mb-10">Datenschutzerklärung</h1>

                <div className="space-y-8 font-body text-stone-700 text-sm leading-relaxed">
                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">1. Verantwortlicher</h2>
                        <p>
                            {c.name}<br />
                            {c.address}<br />
                            E-Mail: {c.email}<br />
                            Telefon: {c.phone}
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">2. Erhebung &amp; Verwendung personenbezogener Daten</h2>
                        <p>
                            Wir verarbeiten personenbezogene Daten ausschließlich zur Bearbeitung
                            Ihrer Kaufanfrage zum Projekt G21, Gaugasse 21 in Wiesbaden. Die
                            Verarbeitung erfolgt auf Grundlage Ihrer Einwilligung (Art. 6 Abs. 1
                            lit. a DSGVO) sowie zur Anbahnung eines Vertrages (Art. 6 Abs. 1 lit. b
                            DSGVO).
                        </p>
                        <ul className="list-disc list-inside mt-3 space-y-1">
                            <li>Name, E-Mail-Adresse, Telefonnummer</li>
                            <li>Angaben zur Wohnungspräferenz und Verwendungsabsicht</li>
                            <li>Optional: Angaben zur Finanzierung und gewünschtem Einzugstermin</li>
                            <li>Ihre Nachricht an uns</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">3. Speicherdauer</h2>
                        <p>
                            Wir speichern Ihre Daten so lange, wie es zur Bearbeitung Ihrer Anfrage
                            sowie zur Erfüllung gesetzlicher Aufbewahrungspflichten erforderlich ist.
                            Anschließend werden die Daten gelöscht oder anonymisiert.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">4. Ihre Rechte</h2>
                        <p>Ihnen stehen folgende Rechte zu:</p>
                        <ul className="list-disc list-inside mt-3 space-y-1">
                            <li>Auskunft (Art. 15 DSGVO)</li>
                            <li>Berichtigung (Art. 16 DSGVO)</li>
                            <li>Löschung (Art. 17 DSGVO)</li>
                            <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
                            <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
                            <li>Widerspruch (Art. 21 DSGVO)</li>
                            <li>Widerruf einer Einwilligung mit Wirkung für die Zukunft</li>
                            <li>Beschwerde bei einer Aufsichtsbehörde</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">5. Weitergabe an Dritte</h2>
                        <p>
                            Eine Weitergabe an Dritte erfolgt nicht, sofern dies nicht zur
                            Bearbeitung Ihrer Anfrage erforderlich ist (z. B. an ggf. eingebundene
                            Notare, Banken oder Vertriebspartner) oder gesetzlich vorgeschrieben.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">6. Hosting &amp; Server-Logs</h2>
                        <p>
                            Beim Aufruf unserer Website werden technisch notwendige Daten erfasst
                            (z. B. IP-Adresse, Datum/Uhrzeit, Browsertyp). Diese Daten werden
                            ausschließlich zur Sicherstellung des Betriebs verarbeitet (Art. 6 Abs. 1
                            lit. f DSGVO).
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">7. Cookies &amp; Tracking</h2>
                        <p>
                            Wir verwenden auf dieser Website zwei Arten von Cookies bzw.
                            vergleichbaren Technologien (lokaler Speicher des Browsers):
                        </p>
                        <ul className="list-disc list-inside mt-3 space-y-1">
                            <li>
                                <strong>Technisch notwendige Cookies:</strong> Sie sind
                                erforderlich, damit grundlegende Funktionen wie Formulareingaben,
                                Sicherheitsmechanismen und das Speichern Ihrer Cookie-Auswahl
                                funktionieren. Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO
                                (berechtigtes Interesse) bzw. § 25 Abs. 2 Nr. 2 TTDSG.
                            </li>
                            <li>
                                <strong>Statistik-Cookies (nur mit Einwilligung):</strong> Zur
                                anonymen Reichweitenmessung und Verbesserung des Angebots setzen
                                wir <strong>PostHog</strong> ein. Erfasst werden u. a. Seitenaufrufe,
                                Klicks, Browser- und Gerätetyp – ohne direkten Personenbezug. Die
                                IP-Adresse wird gekürzt. Anbieter: PostHog Inc., 2261 Market
                                Street #4008, San Francisco, CA 94114, USA. Datenübertragung in
                                Drittländer kann stattfinden (Standardvertragsklauseln). Speicherdauer
                                bis zu 12 Monate. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO,
                                § 25 Abs. 1 TTDSG.
                            </li>
                        </ul>
                        <p className="mt-3">
                            Beim ersten Besuch fragen wir Ihre Einwilligung über einen
                            Cookie-Hinweis ab. Sie können Ihre Auswahl jederzeit über den Link{" "}
                            <strong>„Cookie-Einstellungen"</strong> im Footer ändern oder
                            widerrufen. Ein Widerruf gilt für die Zukunft und beeinflusst nicht
                            die Rechtmäßigkeit der bis dahin erfolgten Verarbeitung.
                        </p>
                    </section>

                    <section>
                        <h2 className="font-heading text-xl text-ink mb-3">8. Kontakt</h2>
                        <p>
                            Bei Fragen zum Datenschutz: <a href={`mailto:${c.email}`} className="text-brand-600 hover:underline">{c.email}</a>
                        </p>
                    </section>

                    <p className="text-xs text-stone-500 pt-4 border-t border-stone-200">
                        Stand: {new Date().toLocaleDateString("de-DE")}.
                    </p>
                </div>
            </div>
            </div>
        </PageShell>
    );
}
